import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LocateFixed, Home, Search, X } from 'lucide-react';
import {
  loadGoogleMapsAPI,
  geocodeAddress,
  fetchPlaceSuggestions,
  createSessionToken,
} from '../../PlaceSearch/helpers/PlacesSearchHelper';
import genericUtility from '../../../../services/utility/GenericUtility';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../../../constants/EnvVariables';
import { useNotification } from '../../contexts/NotificationContext';
import { NotificationType } from '../../../../constants/Enums';
import useLocationPermission from '../../../../hooks/useLocationPermission';
import { useDebounce } from '../../../../hooks/useDebounce';
import '../styles/LocationPinPicker.css';

interface Coords {
  lat: number;
  lng: number;
}

interface LocationPinPickerProps {
  /** Typed delivery address — used as a fallback map center if GPS is unavailable. */
  initialAddress?: string;
  /** Existing pin coordinates, if the user is editing a previously confirmed pin. */
  initialCoords?: Coords | null;
  onConfirm: (coords: Coords) => void;
  onCancel: () => void;
}

/** Fallback center when address geocoding and saved location are both unavailable. */
const DEFAULT_CENTER: Coords = { lat: 12.9716, lng: 77.5946 }; // Bengaluru

const LocationPinPicker: React.FC<LocationPinPickerProps> = ({
  initialAddress,
  initialCoords,
  onConfirm,
  onCancel,
}) => {
  const { triggerNotification } = useNotification();
  const { checkPermissionAndExecute, PermissionModal } = useLocationPermission();
  const generalDbLocation = useSelector((s: any) => s.general.UserLocation);
  // Ref so init() reads the latest Redux value even if it populates after first render.
  const generalDbLocationRef = useRef(generalDbLocation);
  generalDbLocationRef.current = generalDbLocation;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  const [pinCoords, setPinCoords] = useState<Coords | null>(initialCoords ?? null);
  const [isReady, setIsReady] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [isCentering, setIsCentering] = useState(false);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ text: string; place: google.maps.places.Place }>>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const skipNextSearchRef = useRef(false);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!window.google?.maps) {
        try {
          await Promise.race([
            new Promise<void>((res) => loadGoogleMapsAPI(REACT_APP_GOOGLE_MAPS_API_KEY, res)),
            new Promise<void>((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000)),
          ]);
        } catch {
          // Maps failed to load (script error or timeout) — fall back to the WhatsApp step.
          onCancel();
          return;
        }
      }
      if (cancelled || !mapContainerRef.current) return;

      let center = initialCoords;
      if (!center) {
        center = await new Promise<Coords | null>((resolve) => {
          checkPermissionAndExecute(
            async () => {
              try {
                const { latitude, longitude } = await genericUtility.getCurrentUserLocation();
                resolve({ lat: latitude, lng: longitude });
              } catch {
                triggerNotification(
                  "Couldn't get your location — drag the pin to set it manually",
                  NotificationType.INFO,
                );
                resolve(null);
              }
            },
            () => { resolve(null); },
            {
              preTitle: 'Use your location for delivery',
              preSubtitle: "We can place the pin at your current location so it's accurate — you can still drag it to adjust.",
              deniedSubtitle: "Location access helps set an accurate delivery pin for your WhatsApp order. You can still drag the pin manually on the map.",
            },
          );
        });
      }
      if (!center && initialAddress) {
        center = await geocodeAddress(initialAddress);
      }
      const dbLoc = generalDbLocationRef.current;
      if (!center && dbLoc?.userLatitude && dbLoc?.userLongitude) {
        center = { lat: dbLoc.userLatitude, lng: dbLoc.userLongitude };
      }
      if (!center) {
        center = DEFAULT_CENTER;
      }
      if (cancelled || !mapContainerRef.current) return;

      const map = new google.maps.Map(mapContainerRef.current, {
        center,
        zoom: 17,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
      });

      const marker = new google.maps.Marker({
        position: center,
        map,
        draggable: true,
      });

      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        if (pos) setPinCoords({ lat: pos.lat(), lng: pos.lng() });
      });

      mapRef.current = map;
      markerRef.current = marker;
      setPinCoords(center);
      setIsReady(true);
    };

    init();

    return () => {
      cancelled = true;
      if (markerRef.current) {
        google.maps.event.clearInstanceListeners(markerRef.current);
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2 || !isReady) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (!sessionTokenRef.current) sessionTokenRef.current = createSessionToken();
      setIsSearching(true);
      try {
        const results = await fetchPlaceSuggestions(debouncedQuery, sessionTokenRef.current!);
        if (!cancelled) {
          setSuggestions(results);
          setShowDropdown(results.length > 0);
        }
      } catch {
        if (!cancelled) { setSuggestions([]); setShowDropdown(false); }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [debouncedQuery, isReady]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const moveMarkerTo = (coords: Coords, zoom?: number) => {
    mapRef.current?.panTo(coords);
    if (zoom !== undefined) mapRef.current?.setZoom(zoom);
    markerRef.current?.setPosition(coords);
    setPinCoords(coords);
  };

  const handleSelectSuggestion = async (item: { text: string; place: google.maps.places.Place }) => {
    skipNextSearchRef.current = true;
    setQuery(item.text);
    setShowDropdown(false);
    setSuggestions([]);
    sessionTokenRef.current = null;
    try {
      await item.place.fetchFields({ fields: ['location'] });
      const loc = item.place.location;
      if (loc) { moveMarkerTo({ lat: loc.lat(), lng: loc.lng() }, 17); return; }
    } catch { /* fall through */ }
    const coords = await geocodeAddress(item.text);
    if (coords) moveMarkerTo(coords, 17);
    else triggerNotification('Could not locate this address on the map', NotificationType.ERROR);
  };

  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    try {
      const { latitude, longitude } = await genericUtility.getCurrentUserLocation();
      moveMarkerTo({ lat: latitude, lng: longitude });
    } catch (error) {
      const message = typeof error === 'string' ? error : 'Unable to retrieve your location';
      triggerNotification(message, NotificationType.ERROR);
    } finally {
      setIsLocating(false);
    }
  };

  const handleCenterOnAddress = async () => {
    if (!initialAddress?.trim()) return;
    setIsCentering(true);
    try {
      const coords = await geocodeAddress(initialAddress);
      if (!coords) {
        triggerNotification('Could not locate this address on the map', NotificationType.ERROR);
        return;
      }
      moveMarkerTo(coords, 17);
    } finally {
      setIsCentering(false);
    }
  };

  return (
    <div className="lpp-container modal-scrollable">
      <PermissionModal />
      <div className="lpp-map-wrapper">
        <div ref={mapContainerRef} className="lpp-map-container" />
        {!isReady && <div className="lpp-map-loading">Loading map…</div>}
        {isReady && (
          <div className="lpp-search-wrapper" ref={searchWrapperRef}>
            <div className="lpp-search-input-row">
              <Search size={14} className="lpp-search-icon" />
              <input
                className="lpp-search-input"
                type="text"
                placeholder="Search for a location…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setShowDropdown(false)}
              />
              {isSearching && <div className="lpp-gps-spinner lpp-search-spinner" />}
              {query && !isSearching && (
                <button
                  className="lpp-search-clear"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    skipNextSearchRef.current = false;
                    setQuery('');
                    setSuggestions([]);
                    setShowDropdown(false);
                  }}
                  aria-label="Clear search"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {showDropdown && suggestions.length > 0 && (
              <ul className="lpp-suggestions">
                {suggestions.map((s, i) => (
                  <li
                    key={i}
                    className="lpp-suggestion-item"
                    onMouseDown={() => handleSelectSuggestion(s)}
                  >
                    {s.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button
          className="lpp-gps-btn"
          onClick={handleUseCurrentLocation}
          disabled={!isReady || isLocating}
          aria-label="Use my current location"
          title="Use my current location"
        >
          {isLocating ? <div className="lpp-gps-spinner" /> : <LocateFixed size={18} />}
        </button>
        {initialAddress?.trim() && (
          <button
            className="lpp-gps-btn lpp-address-btn"
            onClick={handleCenterOnAddress}
            disabled={!isReady || isCentering}
            aria-label="Center on delivery address"
            title="Center on delivery address"
          >
            {isCentering ? <div className="lpp-gps-spinner" /> : <Home size={18} />}
          </button>
        )}
      </div>

      <div className="lpp-actions">
        <button className="lpp-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="lpp-confirm-btn"
          onClick={() => pinCoords && onConfirm(pinCoords)}
          disabled={!pinCoords}
        >
          Confirm Pin
        </button>
      </div>
    </div>
  );
};

export default LocationPinPicker;
